import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios from 'axios'
import { getStorageToken, removeTokenFromStorage, getTokenStorageType } from '../utils/storageToken'

const login = createAsyncThunk('Auth/LOGIN', async (payload, thunkAPI) => {
    const API_URL = process.env.REACT_APP_AUTH_API_URI
    try {
        const response = await axios.post(`${API_URL}/api/v1/auth/signIn`, {
            userEmail: payload.email,
            userPsw: payload.password,
        })
        return thunkAPI.fulfillWithValue(response.data)
    } catch (error) {
        console.log(error)
        return thunkAPI.rejectWithValue(error)
    }
})

const register = createAsyncThunk('Auth/REGISTER', async (payload, thunkAPI) => {
    const API_URL = process.env.REACT_APP_AUTH_API_URI
    try {
        const response = await axios.post(`${API_URL}/api/v1/auth/register`, {
            userEmail: payload.email,
            userPsw: payload.password,
            userLastName: payload.lastname,
            userFirstName: payload.firstname,
            userLanguage: 'EN',
        })
        return thunkAPI.fulfillWithValue(response.data)
    } catch (error) {
        return thunkAPI.rejectWithValue(error)
    }
})

const resetPassword = createAsyncThunk('AUTH/RESET_PASSWORD', async (payload, thunkAPI) => {
    const API_URL = process.env.REACT_APP_AUTH_API_URI
    try {
        const response = await axios.post(`${API_URL}/api/v1/auth/resetPsw`, {
            t: payload.token,
            psw: payload.password
        })
        return thunkAPI.fulfillWithValue(response.data)
    } catch (error) {
        return thunkAPI.rejectWithValue(error)
    }
})

const sendResetPasswordLink = createAsyncThunk('AUTH/SEND_RESET_PASSWORD_LINK', async (payload, thunkAPI) => {
    const API_URL = process.env.REACT_APP_AUTH_API_URI
    try {
        const response = await axios.post(`${API_URL}/api/v1/auth/requestResetPsw`, {
            userEmail: payload.email
        })
        return thunkAPI.fulfillWithValue(response.data)
    } catch (error) {
        return thunkAPI.rejectWithValue(error)
    }
})

const checkTokenValidity = createAsyncThunk('AUTH/TOKEN_VALIDITY', async (payload, thunkAPI) => {
    const API_URL =   `${process.env.REACT_APP_AUTH_API_URI}/api/v1/user`
    try {
        const response = await axios.get(API_URL, {
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${payload.token}`
            }
        })
        return thunkAPI.fulfillWithValue(response.data)
    } catch (err) {
        return thunkAPI.rejectWithValue(err)
    }
})

const tokenInStorage = getStorageToken()
const initialState = {
    token: tokenInStorage ? tokenInStorage : undefined,
    redirectUrl: undefined,
    isLoading: false,
    error: undefined,
    success: false,
}

export const authSlice = createSlice({
    name: 'Auth',
    initialState,
    reducers: {
        removeToken: (state) => {
            state.token = undefined
        }
    },
    extraReducers(builder) {
        // *** LOGIN *** \\\
        builder.addCase(login.pending, (state) => {
            state.isLoading = true
        })
        builder.addCase(login.fulfilled, (state, action) => {
            state.isLoading = false
            if ('token' in action.payload.data) {
                state.token = action.payload.data.token
            } else {
                removeTokenFromStorage(getTokenStorageType())
                state.token = undefined
            }
            state.success = true
        })
        builder.addCase(login.rejected, (state, action) => {
            state.isLoading = false
            state.error = action.error
            state.success = false
            state.token = undefined
            removeTokenFromStorage(getTokenStorageType())
        })
        // *** REGISTER *** \\\
        builder.addCase(register.pending, (state) => {
            state.isLoading = true
        })
        builder.addCase(register.fulfilled, (state, action) => {
            state.isLoading = false
            if ('token' in action.payload.data) {
                state.token = action.payload.data.token
            } else {
                removeTokenFromStorage(getTokenStorageType())
                state.token = undefined
            }
            state.success = true
        })
        builder.addCase(register.rejected, (state, action) => {
            state.isLoading = false
            state.error = action.error
            state.success = false
            state.token = undefined
            removeTokenFromStorage(getTokenStorageType())
        })
        // *** RESET PASSWORD *** \\\
        builder.addCase(resetPassword.pending, (state) => {
            state.isLoading = true
        })
        builder.addCase(resetPassword.fulfilled, (state) => {
            state.isLoading = false
            state.token = undefined
            removeTokenFromStorage(getTokenStorageType())
            state.success = true
        })
        builder.addCase(resetPassword.rejected, (state, action) => {
            state.isLoading = false
            state.error = action.error
            state.token = undefined
            removeTokenFromStorage(getTokenStorageType())
        })
        // *** SEND RESET PASSWORD LINK *** \\\
        builder.addCase(sendResetPasswordLink.pending, (state) => {
            state.isLoading = true
        })
        builder.addCase(sendResetPasswordLink.fulfilled, (state, action) => {
            state.isLoading = false
            state.token = undefined
            removeTokenFromStorage(getTokenStorageType())
            state.success = true
        })
        builder.addCase(sendResetPasswordLink.rejected, (state, action) => {
            state.isLoading = false
            state.error = action.error
            state.token = undefined
            removeTokenFromStorage(getTokenStorageType())
        })
        // *** CHECK TOKEN VALIDITY *** \\\
        builder.addCase(checkTokenValidity.pending, (state) => {
            state.isLoading = true
        })
        builder.addCase(checkTokenValidity.rejected, (state, action) => {
            state.isLoading = false
            removeTokenFromStorage(getTokenStorageType())
            state.token = undefined
            state.success = false
            state.error = action.error
        })
        builder.addCase(checkTokenValidity.fulfilled, (state) => {
            state.isLoading = false
            state.success = true
        })
    }
})

export {
    login,
    register,
    resetPassword,
    sendResetPasswordLink,
    checkTokenValidity
}
export const { removeToken } = authSlice.actions
export default authSlice.reducer
